// frontend/super-admin/src/pages/Tenants.jsx
import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Select,
  message,
  Popconfirm,
  Card,
  Switch,
  InputNumber,
  Row,
  Col,
  Divider,
  Alert,
  Typography,
  Tooltip,
  Badge
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SettingOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  KeyOutlined  // ✅ ADD THIS
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [form] = Form.useForm();
  const [planForm] = Form.useForm();

  // ✅ NEW: Activation Modal States
  const [activationModalVisible, setActivationModalVisible] = useState(false);
  const [activationForm] = Form.useForm();
  const [selectedTenantForActivation, setSelectedTenantForActivation] = useState(null);
  const [activationMode, setActivationMode] = useState('duration'); // 'duration', 'dates', 'payment'

  // ✅ NEW: Password Reset Modal States
  const [passwordResetModalVisible, setPasswordResetModalVisible] = useState(false);
  const [passwordResetForm] = Form.useForm();
  const [selectedTenantForPasswordReset, setSelectedTenantForPasswordReset] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    fetchTenants();
    fetchAvailablePlans();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/tenants');
      setTenants(response.data.data);
    } catch (error) {
      message.error('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const response = await api.get('/api/admin/plans/available');
      setPlans(response.data.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleCreate = () => {
    setEditingTenant(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    form.setFieldsValue(tenant);
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editingTenant) {
        await api.put(`/api/admin/tenants/${editingTenant.id}`, values);
        message.success('Tenant updated successfully');
      } else {
        await api.post('/api/admin/tenants', values);
        message.success('Tenant created successfully');
      }

      setModalVisible(false);
      fetchTenants();
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/tenants/${id}`);
      message.success('Tenant deleted successfully');
      fetchTenants();
    } catch (error) {
      message.error('Failed to delete tenant');
    }
  };

  // ✅ NEW: Approve Tenant
  const handleApprove = async (tenantId) => {
    try {
      await api.put(`/api/admin/tenants/${tenantId}/approve`);
      message.success('✅ Tenant approved successfully!');
      fetchTenants();
    } catch (error) {
      message.error('Failed to approve tenant');
    }
  };

  // ✅ NEW: Reject Tenant (with reason)
  const handleReject = (tenant) => {
    Modal.confirm({
      title: `Reject ${tenant.businessName}?`,
      content: (
        <Input.TextArea
          id="rejection-reason"
          placeholder="Enter rejection reason (optional)"
          rows={3}
        />
      ),
      onOk: async () => {
        const reason = document.getElementById('rejection-reason').value;
        
        try {
          await api.put(`/api/admin/tenants/${tenant.id}/reject`, {
            reason: reason || 'Not specified'
          });
          
          message.success('❌ Tenant rejected');
          fetchTenants();
        } catch (error) {
          message.error('Failed to reject tenant');
        }
      }
    });
  };

  // ✅ NEW: Manage Plan & Features
  const handleManagePlan = (tenant) => {
    setSelectedTenant(tenant);
    
    // Set current values
    const currentPlan = tenant.currentSubscription?.plan;
    const customFeatures = tenant.customFeatures || {};
    const customLimits = tenant.customLimits || {};

    planForm.setFieldsValue({
      plan_id: currentPlan?.id,
      // Features
      'feature_whatsapp': customFeatures.whatsapp !== undefined 
        ? customFeatures.whatsapp 
        : currentPlan?.features?.whatsapp,
      'feature_instagram': customFeatures.instagram !== undefined
        ? customFeatures.instagram
        : currentPlan?.features?.instagram,
      'feature_catalog': customFeatures.catalog !== undefined
        ? customFeatures.catalog
        : currentPlan?.features?.catalog,
      'feature_chatbot': customFeatures.chatbot !== undefined
        ? customFeatures.chatbot
        : currentPlan?.features?.chatbot,
      'feature_campaigns': customFeatures.campaigns !== undefined
        ? customFeatures.campaigns
        : currentPlan?.features?.campaigns,
      'feature_analytics': customFeatures.analytics !== undefined
        ? customFeatures.analytics
        : currentPlan?.features?.analytics,
      'feature_api': customFeatures.api !== undefined
        ? customFeatures.api
        : currentPlan?.features?.api,
      'feature_team': customFeatures.team !== undefined
        ? customFeatures.team
        : currentPlan?.features?.team,
      
      // ✅ NEW: Add these
      'feature_contacts': customFeatures.contacts !== undefined
        ? customFeatures.contacts
        : currentPlan?.features?.contacts !== false,  // Default true
      'feature_reminders': customFeatures.reminders !== undefined
        ? customFeatures.reminders
        : currentPlan?.features?.reminders !== false,  // Default true
      
      // Limits
      contacts_limit: customLimits.contacts_limit || currentPlan?.contacts_limit,
      messages_limit: customLimits.messages_limit || currentPlan?.messages_limit,
      team_members_limit: customLimits.team_members_limit || currentPlan?.team_members_limit,
      chatbot_flows_limit: customLimits.chatbot_flows_limit || currentPlan?.chatbot_flows_limit,
      campaigns_limit: customLimits.campaigns_limit || currentPlan?.campaigns_limit,
      api_calls_limit: customLimits.api_calls_limit || currentPlan?.api_calls_limit
    });

    setPlanModalVisible(true);
  };

  // ✅ NEW: Save Plan & Features
  const handleSavePlanAndFeatures = async () => {
    try {
      const values = planForm.getFieldsValue();

      const payload = {
        plan_id: values.plan_id,
        custom_features: {
          whatsapp: values.feature_whatsapp,
          instagram: values.feature_instagram,
          catalog: values.feature_catalog,
          chatbot: values.feature_chatbot,
          campaigns: values.feature_campaigns,
          analytics: values.feature_analytics,
          api: values.feature_api,
          team: values.feature_team,
          contacts: values.feature_contacts,      // ✅ NEW
          reminders: values.feature_reminders     // ✅ NEW
        },
        custom_limits: {
          contacts_limit: values.contacts_limit,
          messages_limit: values.messages_limit,
          team_members_limit: values.team_members_limit,
          chatbot_flows_limit: values.chatbot_flows_limit,
          campaigns_limit: values.campaigns_limit,
          api_calls_limit: values.api_calls_limit
        }
      };

      await api.put(
        `/api/admin/tenants/${selectedTenant.id}/plan-features`,
        payload
      );

      message.success('✅ Plan & features updated successfully!');
      setPlanModalVisible(false);
      fetchTenants();

    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update');
    }
  };

  // ==========================================
  // ✅ SUBSCRIPTION CONTROL HANDLERS
  // ==========================================

  const handleActivate = async (tenantId, days) => {
    try {
      const planSlug = days >= 365 ? 'advanced' : 'growth';
      
      await api.post(
        `/api/admin/tenants/${tenantId}/activate-subscription`,
        { 
          planSlug, 
          durationDays: days 
        }
      );
      
      message.success(`✅ Subscription activated for ${days} days!`);
      fetchTenants();
      
    } catch (error) {
      message.error(error.response?.data?.message || 'Activation failed');
    }
  };

  const handleExtend = async (tenantId, days) => {
    try {
      await api.post(
        `/api/admin/tenants/${tenantId}/extend-subscription`,
        { days }
      );
      
      message.success(`✅ Subscription extended by ${days} days!`);
      fetchTenants();
      
    } catch (error) {
      message.error(error.response?.data?.message || 'Extension failed');
    }
  };

  const handleDisable = (tenantId) => {
    Modal.confirm({
      title: '⚠️ Disable Subscription?',
      content: 'User will lose access immediately. Continue?',
      okText: 'Yes, Disable',
      okType: 'danger',
      centered: true,
      onOk: async () => {
        try {
          await api.post(`/api/admin/tenants/${tenantId}/disable-subscription`);
          message.success('❌ Subscription disabled');
          fetchTenants();
        } catch (error) {
          message.error(error.response?.data?.message || 'Failed to disable');
        }
      }
    });
  };

  // ✅ NEW: Open Activation Modal
  const openActivationModal = (tenant) => {
    setSelectedTenantForActivation(tenant);
    setActivationMode('duration');
    
    // Set default values
    activationForm.setFieldsValue({
      planSlug: tenant.plan || 'starter',
      durationDays: 30,
      trialDays: 0,
      startDate: null,
      endDate: null,
      amountPaid: null
    });
    
    setActivationModalVisible(true);
  };

  // ✅ NEW: Handle Activation Submit
  const handleActivationSubmit = async () => {
    try {
      const values = activationForm.getFieldsValue();
      const tenantId = selectedTenantForActivation.id;

      let endpoint, payload;

      // ✅ Mode 1: Payment-Based
      if (activationMode === 'payment' && values.amountPaid) {
        endpoint = `/api/admin/tenants/${tenantId}/activate-by-payment`;
        payload = {
          amountPaid: values.amountPaid,
          planSlug: values.planSlug,
          paymentMethod: values.paymentMethod || 'manual',
          transactionId: values.transactionId
        };
      } 
      // ✅ Mode 2: Custom Dates
      else if (activationMode === 'dates' && values.startDate && values.endDate) {
        endpoint = `/api/admin/tenants/${tenantId}/activate-subscription`;
        payload = {
          planSlug: values.planSlug,
          startDate: values.startDate,
          endDate: values.endDate,
          trialDays: values.trialDays || 0
        };
      } 
      // ✅ Mode 3: Duration
      else {
        endpoint = `/api/admin/tenants/${tenantId}/activate-subscription`;
        payload = {
          planSlug: values.planSlug,
          durationDays: values.durationDays || 30,
          trialDays: values.trialDays || 0
        };
      }

      await api.post(endpoint, payload);
      
      message.success('✅ Subscription activated successfully!');
      setActivationModalVisible(false);
      fetchTenants();
      
    } catch (error) {
      message.error(error.response?.data?.message || 'Activation failed');
    }
  };

  // ==========================================
  // ✅ PASSWORD RESET HANDLERS
  // ==========================================

  const openPasswordResetModal = (tenant) => {
    setSelectedTenantForPasswordReset(tenant);
    passwordResetForm.resetFields();
    setPasswordResetModalVisible(true);
  };

  const handlePasswordResetSubmit = async () => {
    try {
      await passwordResetForm.validateFields();
      setResettingPassword(true);

      const values = passwordResetForm.getFieldsValue();
      const tenantId = selectedTenantForPasswordReset.id;

      await api.put(`/api/admin/tenants/${tenantId}/reset-password`, {
        newPassword: values.newPassword
      });

      message.success(`✅ Password reset successfully for ${selectedTenantForPasswordReset.businessName}`);
      
      Modal.success({
        title: '🔐 Password Reset Successful',
        content: (
          <div>
            <p style={{ fontSize: 15, marginBottom: 12 }}>
              Password has been reset for <strong>{selectedTenantForPasswordReset.businessName}</strong>
            </p>
            <div style={{ 
              background: '#f0f0f0', 
              padding: 12, 
              borderRadius: 6,
              marginBottom: 12
            }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Email:</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {selectedTenantForPasswordReset.email}
              </div>
            </div>
            <Alert
              message="⚠️ Important"
              description="Please share the new password with the tenant securely."
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
            />
          </div>
        ),
        centered: true,
        okText: 'Done'
      });

      setPasswordResetModalVisible(false);

    } catch (error) {
      console.error('Password reset error:', error);
      
      if (error.errorFields) {
        message.error('Please fill all required fields correctly');
      } else {
        message.error(error.response?.data?.message || 'Failed to reset password');
      }
    } finally {
      setResettingPassword(false);
    }
  };

  // ✅ Optional: Auto-generate password
  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    passwordResetForm.setFieldsValue({
      newPassword: password,
      confirmPassword: password
    });
    
    message.success('Password generated!');
  };

  const columns = [
    {
      title: 'Business Name',
      dataIndex: 'businessName',
      key: 'businessName',
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          {record.customFeatures && (
            <Tag color="orange" style={{ fontSize: 10, marginTop: 4 }}>
              Custom Access
            </Tag>
          )}
        </div>
      )
    },
    {
      title: 'Owner',
      dataIndex: 'ownerName',
      key: 'ownerName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => {
        const planName = record.currentSubscription?.plan?.name || record.plan || 'No Plan';
        const hasCustom = record.customFeatures || record.customLimits;
        
        return (
          <Space direction="vertical" size={2}>
            <Tag color="blue">{planName.toUpperCase()}</Tag>
            {hasCustom && (
              <Tooltip title="Super Admin has customized features/limits">
                <Tag color="gold" icon={<SettingOutlined />} style={{ fontSize: 10 }}>
                  Customized
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Approval Status',
      key: 'approvalStatus',
      render: (_, record) => {
        const statusConfig = {
          pending: { color: 'orange', icon: '⏳', text: 'PENDING' },
          approved: { color: 'green', icon: '✅', text: 'APPROVED' },
          rejected: { color: 'red', icon: '❌', text: 'REJECTED' }
        };
        
        const config = statusConfig[record.approvalStatus] || statusConfig.pending;
        
        return (
          <Tag color={config.color}>
            {config.icon} {config.text}
          </Tag>
        );
      }
    },
    {
      title: 'Subscription',
      key: 'subscription',
      render: (_, record) => {
        const now = new Date();
        const endDate = record.planEndDate ? new Date(record.planEndDate) : null;
        const isExpired = endDate && endDate < now;
        const daysLeft = endDate 
          ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))
          : null;

        return (
          <Space direction="vertical" size={2}>
            {record.planStatus === 'trial' && record.trialEndsAt && (
              <Tag color="orange" style={{ fontSize: 11 }}>
                Trial: {new Date(record.trialEndsAt).toLocaleDateString()}
              </Tag>
            )}
            
            {record.planStatus === 'active' && endDate && (
              <Tag color={isExpired ? 'red' : daysLeft < 7 ? 'orange' : 'green'} style={{ fontSize: 11 }}>
                {isExpired 
                  ? `Expired ${Math.abs(daysLeft)} days ago` 
                  : `${daysLeft} days left`}
              </Tag>
            )}

            {endDate && (
              <Text style={{ fontSize: 11, color: '#999' }}>
                Until: {endDate.toLocaleDateString()}
              </Text>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 500, // ✅ Increased width
      render: (_, record) => (
        <Space size="small" wrap>
          
          {/* Approval Buttons */}
          {record.approvalStatus === 'pending' && (
            <>
              <Tooltip title="Approve Tenant">
                <Button
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApprove(record.id)}
                  size="small"
                  style={{ color: '#52c41a', borderColor: '#52c41a' }}
                />
              </Tooltip>

              <Tooltip title="Reject Tenant">
                <Button
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(record)}
                  size="small"
                  danger
                />
              </Tooltip>
            </>
          )}

          {/* Subscription Control */}
          {record.approvalStatus === 'approved' && (
            <>
              <Tooltip title="Activate/Manage Subscription">
                <Button 
                  type="primary"
                  size="small"
                  onClick={() => openActivationModal(record)}
                  icon={<CrownOutlined />}
                >
                  Activate
                </Button>
              </Tooltip>

              <Tooltip title="Disable Service">
                <Button 
                  danger
                  size="small"
                  onClick={() => handleDisable(record.id)}
                >
                  ❌
                </Button>
              </Tooltip>
            </>
          )}

          {/* Edit Button */}
          <Tooltip title="Edit Tenant">
            <Button 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>

          {/* ✅ NEW: Reset Password Button */}
          <Tooltip title="Reset Password">
            <Button 
              icon={<KeyOutlined />} 
              onClick={() => openPasswordResetModal(record)}
              size="small"
              style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
            />
          </Tooltip>

          {/* Manage Plan & Features */}
          <Tooltip title="Manage Plan & Features">
            <Button 
              icon={<CrownOutlined />} 
              onClick={() => handleManagePlan(record)}
              size="small"
              type="primary"
            />
          </Tooltip>

          {/* Delete Button */}
          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Delete Tenant">
              <Button 
                icon={<DeleteOutlined />} 
                danger
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h1>👥 Tenants Management</h1>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Add Tenant
        </Button>
      </div>

      <Table 
        dataSource={tenants}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      {/* Create/Edit Tenant Modal */}
      <Modal
        title={editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item name="businessName" label="Business Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="ownerName" label="Owner Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>

          {!editingTenant && (
            <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>

          {/* ✅ UPDATED: Initial Plan - only 4 options, growth & advanced removed */}
          <Form.Item name="plan" label="Initial Plan" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="free">Free</Select.Option>
              <Select.Option value="starter">Starter</Select.Option>
              <Select.Option value="professional">Professional</Select.Option>
              <Select.Option value="enterprise">Enterprise</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTenant ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ✅ NEW: Advanced Activation Modal */}
      <Modal
        title={
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            Activate Subscription - {selectedTenantForActivation?.businessName}
          </Space>
        }
        open={activationModalVisible}
        onCancel={() => setActivationModalVisible(false)}
        onOk={handleActivationSubmit}
        width={600}
        okText="Activate"
      >
        <Form form={activationForm} layout="vertical">
          
          {/* Activation Mode Selector */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Activation Method:</Text>
              <Space wrap>
                <Button 
                  type={activationMode === 'duration' ? 'primary' : 'default'}
                  onClick={() => setActivationMode('duration')}
                  size="small"
                >
                  📅 Duration (Days)
                </Button>
                <Button 
                  type={activationMode === 'dates' ? 'primary' : 'default'}
                  onClick={() => setActivationMode('dates')}
                  size="small"
                >
                  📆 Custom Dates
                </Button>
                <Button 
                  type={activationMode === 'payment' ? 'primary' : 'default'}
                  onClick={() => setActivationMode('payment')}
                  size="small"
                >
                  💰 Payment Amount
                </Button>
              </Space>
            </Space>
          </Card>

          {/* ✅ UPDATED: Plan Selection - growth/advanced removed */}
          <Form.Item 
            name="planSlug" 
            label="Plan"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="free">🆓 Free</Select.Option>
              <Select.Option value="starter">🌱 Starter</Select.Option>
              <Select.Option value="professional">💎 Professional</Select.Option>
              <Select.Option value="enterprise">👑 Enterprise</Select.Option>
            </Select>
          </Form.Item>

          {/* Mode 1: Duration */}
          {activationMode === 'duration' && (
            <>
              <Form.Item 
                name="durationDays" 
                label="Duration (Days)"
                rules={[{ required: true }]}
              >
                <InputNumber 
                  min={1} 
                  max={3650}
                  style={{ width: '100%' }}
                  placeholder="e.g., 30"
                  addonAfter="days"
                />
              </Form.Item>

              <Form.Item 
                name="trialDays" 
                label="Trial Period (Optional)"
              >
                <InputNumber 
                  min={0}
                  max={90}
                  style={{ width: '100%' }}
                  placeholder="e.g., 14"
                  addonAfter="days trial"
                />
              </Form.Item>

              <Alert 
                message="Quick Options" 
                description={
                  <Space wrap style={{ marginTop: 8 }}>
                    <Button size="small" onClick={() => activationForm.setFieldsValue({ durationDays: 7 })}>
                      7 Days
                    </Button>
                    <Button size="small" onClick={() => activationForm.setFieldsValue({ durationDays: 30 })}>
                      1 Month
                    </Button>
                    <Button size="small" onClick={() => activationForm.setFieldsValue({ durationDays: 90 })}>
                      3 Months
                    </Button>
                    <Button size="small" onClick={() => activationForm.setFieldsValue({ durationDays: 180 })}>
                      6 Months
                    </Button>
                    <Button size="small" onClick={() => activationForm.setFieldsValue({ durationDays: 365 })}>
                      1 Year
                    </Button>
                  </Space>
                }
                type="info" 
                showIcon
                style={{ marginTop: 12 }}
              />
            </>
          )}

          {/* Mode 2: Custom Dates */}
          {activationMode === 'dates' && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    name="startDate" 
                    label="Start Date"
                    rules={[{ required: true }]}
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    name="endDate" 
                    label="End Date"
                    rules={[{ required: true }]}
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item 
                name="trialDays" 
                label="Trial Period (Optional)"
              >
                <InputNumber 
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="e.g., 14"
                  addonAfter="days trial"
                />
              </Form.Item>
            </>
          )}

          {/* Mode 3: Payment Amount */}
          {activationMode === 'payment' && (
            <>
              <Form.Item 
                name="amountPaid" 
                label="Amount Paid (₹)"
                rules={[{ required: true }]}
              >
                <InputNumber 
                  min={1}
                  style={{ width: '100%' }}
                  placeholder="e.g., 999"
                  prefix="₹"
                />
              </Form.Item>

              <Form.Item 
                name="paymentMethod" 
                label="Payment Method"
              >
                <Select placeholder="Select method">
                  <Select.Option value="manual">Manual/Cash</Select.Option>
                  <Select.Option value="bank_transfer">Bank Transfer</Select.Option>
                  <Select.Option value="upi">UPI</Select.Option>
                  <Select.Option value="razorpay">Razorpay</Select.Option>
                  <Select.Option value="other">Other</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                name="transactionId" 
                label="Transaction ID (Optional)"
              >
                <Input placeholder="Payment reference number" />
              </Form.Item>

              <Alert 
                message="Automatic Duration Calculation" 
                description={
                  <div>
                    <p style={{ marginBottom: 8 }}>Days will be calculated based on amount:</p>
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                      <li>₹999 = 30 days (Starter)</li>
                      <li>₹4999 = 180 days (Growth)</li>
                      <li>₹9999+ = 365 days (Advanced)</li>
                      <li>Custom: ₹33/day</li>
                    </ul>
                  </div>
                }
                type="success" 
                showIcon
                style={{ marginTop: 12 }}
              />
            </>
          )}

        </Form>
      </Modal>

      {/* ✅ NEW: Manage Plan & Features Modal */}
      <Modal
        title={
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            Manage Plan & Features - {selectedTenant?.businessName}
          </Space>
        }
        open={planModalVisible}
        onCancel={() => setPlanModalVisible(false)}
        onOk={handleSavePlanAndFeatures}
        width={800}
        okText="Save Changes"
      >
        <Form form={planForm} layout="vertical">
          
          <Alert
            message="Super Admin Control"
            description="You can override plan defaults for this specific tenant. Custom settings will take priority over plan settings."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          {/* Plan Selection */}
          <Card size="small" title="📦 Base Plan" style={{ marginBottom: 16 }}>
            <Form.Item 
              name="plan_id" 
              label="Subscription Plan"
              rules={[{ required: true, message: 'Please select a plan' }]}
            >
              <Select 
                placeholder="Select plan"
                size="large"
              >
                {plans.map(plan => (
                  <Select.Option key={plan.id} value={plan.id}>
                    <Space>
                      <CrownOutlined />
                      {plan.name} - ₹{plan.price}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Card>

          <Divider />

          {/* Custom Features */}
          <Card size="small" title="✨ Custom Feature Access" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item 
                  name="feature_whatsapp" 
                  label="WhatsApp"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="feature_instagram" 
                  label="Instagram"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="feature_catalog" 
                  label="Product Catalog"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="feature_chatbot" 
                  label="Chatbot Builder"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="feature_campaigns" 
                  label="Campaigns"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="feature_analytics" 
                  label="Advanced Analytics"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="feature_api" 
                  label="API Access"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="feature_team" 
                  label="Team Management"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>

              {/* ✅ NEW: Contacts Management */}
              <Col span={12}>
                <Form.Item 
                  name="feature_contacts" 
                  label="Contacts Management"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>

              {/* ✅ NEW: Reminders */}
              <Col span={12}>
                <Form.Item 
                  name="feature_reminders" 
                  label="Reminders"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="✓" unCheckedChildren="✗" />
                </Form.Item>
              </Col>

            </Row>
          </Card>

          <Divider />

          {/* Custom Limits */}
          <Card size="small" title="📊 Custom Limits">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item 
                  name="contacts_limit" 
                  label="Contacts Limit"
                  help="Set -1 for unlimited"
                >
                  <InputNumber 
                    min={-1} 
                    style={{ width: '100%' }}
                    placeholder="e.g., 5000"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="messages_limit" 
                  label="Messages/Month Limit"
                  help="Set -1 for unlimited"
                >
                  <InputNumber 
                    min={-1} 
                    style={{ width: '100%' }}
                    placeholder="e.g., 10000"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="team_members_limit" 
                  label="Team Members Limit"
                >
                  <InputNumber 
                    min={-1} 
                    style={{ width: '100%' }}
                    placeholder="e.g., 10"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="chatbot_flows_limit" 
                  label="Chatbot Flows Limit"
                >
                  <InputNumber 
                    min={-1} 
                    style={{ width: '100%' }}
                    placeholder="e.g., 5"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="campaigns_limit" 
                  label="Campaigns Limit"
                >
                  <InputNumber 
                    min={-1} 
                    style={{ width: '100%' }}
                    placeholder="e.g., 20"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="api_calls_limit" 
                  label="API Calls Limit"
                >
                  <InputNumber 
                    min={-1} 
                    style={{ width: '100%' }}
                    placeholder="e.g., 50000"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

        </Form>
      </Modal>

      {/* ✅ NEW: Password Reset Modal */}
      <Modal
        title={
          <Space>
            <KeyOutlined style={{ color: '#fa8c16', fontSize: 20 }} />
            Reset Password - {selectedTenantForPasswordReset?.businessName}
          </Space>
        }
        open={passwordResetModalVisible}
        onCancel={() => setPasswordResetModalVisible(false)}
        onOk={handlePasswordResetSubmit}
        confirmLoading={resettingPassword}
        okText="Reset Password"
        okButtonProps={{ 
          danger: true,
          style: { background: '#fa8c16', borderColor: '#fa8c16' }
        }}
        width={500}
      >
        {selectedTenantForPasswordReset && (
          <div>
            <Alert
              message="⚠️ Security Notice"
              description={`You are about to reset the password for ${selectedTenantForPasswordReset.businessName} (${selectedTenantForPasswordReset.email}). The tenant will need to use the new password to login.`}
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form
              form={passwordResetForm}
              layout="vertical"
            >
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter new password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                  { 
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                    message: 'Password must contain uppercase, lowercase and number'
                  }
                ]}
              >
                <Input.Password
                  placeholder="Enter new password (min 6 characters)"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Confirm new password"
                  size="large"
                />
              </Form.Item>

              {/* ✅ Auto-generate Password Button */}
              <Button
                onClick={generatePassword}
                block
                style={{ marginBottom: 16 }}
                icon={<KeyOutlined />}
              >
                🎲 Generate Strong Password
              </Button>
            </Form>

            <Alert
              message="💡 Password Requirements"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 20, fontSize: 12 }}>
                  <li>Minimum 6 characters</li>
                  <li>Must contain uppercase letter (A-Z)</li>
                  <li>Must contain lowercase letter (a-z)</li>
                  <li>Must contain number (0-9)</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>
        )}
      </Modal>

    </div>
  );
}